import type { CSSVariablesResolver, MantineProviderProps, MantineThemeOverride } from '@mantine/core';
import { APP_BRAND } from './brand';
import { appColorSchemeManager } from './colorScheme';

declare module '@mantine/core' {
  export interface MantineThemeOther {
    appToolbarBg: string;
    appDrawerSurface: string;
    appTextMuted: string;
    appTextInfo: string;
    appActionDisabled: string;
    appAiReviewBg: string;
    dashboardNotStarted: string;
    dashboardInProgress: string;
    dashboardCompleted: string;
  }
}

/** Light-mode defaults on `theme.other` (use CSS vars for scheme-aware UI). */
const themeOther = {
  appToolbarBg: APP_BRAND.surfaces.light.toolbar,
  appDrawerSurface: APP_BRAND.surfaces.light.drawer,
  appTextMuted: APP_BRAND.text.light.muted,
  appTextInfo: APP_BRAND.text.light.info,
  appActionDisabled: APP_BRAND.surfaces.light.disabledAction,
  appAiReviewBg: APP_BRAND.aiReview.light.background,
  dashboardNotStarted: APP_BRAND.dashboard.light.notStarted,
  dashboardInProgress: APP_BRAND.dashboard.light.inProgress,
  dashboardCompleted: APP_BRAND.dashboard.light.completed,
} satisfies MantineThemeOverride['other'];

const sharedCssVariables = {
  '--app-font-family': APP_BRAND.fonts.sans,
  '--app-font-family-mono': APP_BRAND.fonts.mono,
  '--gantt-location-bg': APP_BRAND.gantt.location.bg,
  '--gantt-location-border': APP_BRAND.gantt.location.border,
  '--gantt-activity-bg': APP_BRAND.gantt.activity.bg,
  '--gantt-activity-border': APP_BRAND.gantt.activity.border,
  '--gantt-completed-bg': APP_BRAND.gantt.completed.bg,
  '--gantt-completed-border': APP_BRAND.gantt.completed.border,
  '--gantt-critical-border': APP_BRAND.gantt.critical.border,
  '--gantt-critical-shadow': APP_BRAND.gantt.critical.shadow,
  '--gantt-link-fs': APP_BRAND.gantt.links.finishStart,
  '--gantt-link-ss': APP_BRAND.gantt.links.startStart,
  '--gantt-link-ff': APP_BRAND.gantt.links.finishFinish,
} as const;

const lightCssVariables = {
  '--app-shell-bg': APP_BRAND.surfaces.light.appShell,
  '--app-toolbar-bg': APP_BRAND.surfaces.light.toolbar,
  '--app-drawer-surface': APP_BRAND.surfaces.light.drawer,
  '--app-card-bg': APP_BRAND.surfaces.light.card,
  '--app-text-muted': APP_BRAND.text.light.muted,
  '--app-text-info': APP_BRAND.text.light.info,
  '--app-action-disabled': APP_BRAND.surfaces.light.disabledAction,
  '--app-ai-review-bg': APP_BRAND.aiReview.light.background,
  '--app-dashboard-not-started': APP_BRAND.dashboard.light.notStarted,
  '--app-dashboard-in-progress': APP_BRAND.dashboard.light.inProgress,
  '--app-dashboard-completed': APP_BRAND.dashboard.light.completed,
  '--app-nav-active': APP_BRAND.nav.light.active,
  '--app-nav-active-border': APP_BRAND.nav.light.activeBorder,
  '--app-nav-inactive': APP_BRAND.nav.light.inactive,
  '--app-nav-hover-text': APP_BRAND.nav.light.hoverText,
  '--app-nav-menu-hover-bg': APP_BRAND.nav.light.menuHoverBg,
  '--app-nav-menu-hover-border': APP_BRAND.nav.light.menuHoverBorder,
} as const;

const darkCssVariables = {
  '--app-shell-bg': APP_BRAND.surfaces.dark.appShell,
  '--app-toolbar-bg': APP_BRAND.surfaces.dark.toolbar,
  '--app-drawer-surface': APP_BRAND.surfaces.dark.drawer,
  '--app-card-bg': APP_BRAND.surfaces.dark.card,
  '--app-text-muted': APP_BRAND.text.dark.muted,
  '--app-text-info': APP_BRAND.text.dark.info,
  '--app-action-disabled': APP_BRAND.surfaces.dark.disabledAction,
  '--app-ai-review-bg': APP_BRAND.aiReview.dark.background,
  '--app-dashboard-not-started': APP_BRAND.dashboard.dark.notStarted,
  '--app-dashboard-in-progress': APP_BRAND.dashboard.dark.inProgress,
  '--app-dashboard-completed': APP_BRAND.dashboard.dark.completed,
  '--app-nav-active': APP_BRAND.nav.dark.active,
  '--app-nav-active-border': APP_BRAND.nav.dark.activeBorder,
  '--app-nav-inactive': APP_BRAND.nav.dark.inactive,
  '--app-nav-hover-text': APP_BRAND.nav.dark.hoverText,
  '--app-nav-menu-hover-bg': APP_BRAND.nav.dark.menuHoverBg,
  '--app-nav-menu-hover-border': APP_BRAND.nav.dark.menuHoverBorder,
} as const;

export const appMantineTheme: MantineThemeOverride = {
  primaryColor: 'brand',
  colors: {
    brand: [...APP_BRAND.primaryShades],
  },
  fontFamily: APP_BRAND.fonts.sans,
  fontFamilyMonospace: APP_BRAND.fonts.mono,
  headings: {
    fontFamily: APP_BRAND.fonts.sans,
  },
  defaultRadius: 'sm',
  other: themeOther,
};

/** Scheme-aware `--app-*` tokens on :root (usable outside Mantine components). */
export const appCssVariablesResolver: CSSVariablesResolver = () => ({
  variables: sharedCssVariables,
  light: lightCssVariables,
  dark: darkCssVariables,
});

export const getAppMantineProps = (): MantineProviderProps => ({
  theme: appMantineTheme,
  defaultColorScheme: 'auto',
  colorSchemeManager: appColorSchemeManager,
  cssVariablesResolver: appCssVariablesResolver,
});
